import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Hammer, Home, HardHat } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "laborer" ? "laborer" : "local";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "Moscow, ID", phone: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      toast.success(`Welcome, ${user.name}!`);
      navigate(role === "laborer" ? "/profile" : "/feed", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`role-${value}-card`}
      className={`text-left p-4 rounded-2xl border-2 transition-all ${
        role === value ? "border-[#3E2A4E] bg-orange-50/60" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <Icon size={22} className={role === value ? "text-[#3E2A4E]" : "text-slate-500"} />
      <div className="font-heading font-bold mt-2">{title}</div>
      <div className="text-xs text-slate-600 mt-1">{desc}</div>
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#3E2A4E] text-[#D9FF3D] grid place-items-center">
            <Hammer size={18} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Day Hire</span>
        </Link>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Create your account</h1>
        <p className="text-sm text-slate-600 mt-1">Choose how you'll use Day Hire.</p>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <RoleCard value="local" icon={Home} title="I'm a Local" desc="Post tasks and hire nearby help." />
          <RoleCard value="laborer" icon={HardHat} title="I'm a Helper" desc="Find jobs and get paid quickly." />
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.name} onChange={update("name")} data-testid="register-name-input" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email-input" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label htmlFor="password">Password (min 6 chars)</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="register-password-input" className="mt-1.5 h-11" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" value={form.city} onChange={update("city")} data-testid="register-city-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="register-phone-input" className="mt-1.5 h-11" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full h-11 bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full btn-primary-glow">
            {loading ? "Creating account…" : `Continue as ${role === "local" ? "a Local" : "a Helper"}`}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="flex-1 h-px bg-slate-200" />
          <span>OR</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        <GoogleButton role={role} testid="register-google-btn" label={`Sign up with Google as ${role === "local" ? "Local" : "Helper"}`} />

        <p className="mt-6 text-sm text-slate-600">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-[#3E2A4E] hover:underline" data-testid="register-to-login-link">Log in</Link>
        </p>
      </div>
    </div>
  );
}
